import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { motion } from 'framer-motion';
import {
  FaGraduationCap,
  FaUniversity,
  FaCode,
  FaDatabase,
  FaBrain,
  FaLaptopCode,
  FaProjectDiagram,
  FaAward
} from 'react-icons/fa';

const Education = ({ currentTheme }) => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const skills = [
    { name: "Frontend Development", progress: 90 },
    { name: "Backend Development", progress: 85 },
    { name: "Database Management", progress: 88 },
    { name: "System Architecture", progress: 82 },
    { name: "Software Testing", progress: 85 }
  ];

  const projects = [
    {
      title: "Telemedicine Web Application",
      desc: "Developed a web application for remote medical consultations, enhancing patient-doctor interactions through video conferencing and secure data storage."
    },
    {
      title: "JKUAT Hackathon Participation",
      desc: "Participated in the JKUAT hackathon, focusing on innovative solutions using AI and data analytics to address real-world challenges."
    }
  ];

  return (
    <Container fluid className="py-5 position-relative overflow-hidden" id='education' style={{ backgroundColor: currentTheme.background }}>
      <div className="position-absolute w-100 h-100 top-0 start-0" style={{ zIndex: -1 }}>
        <motion.div
          animate={{
            background: [
              `linear-gradient(45deg, ${currentTheme.primary} 0%, ${currentTheme.secondary} 100%)`,
              `linear-gradient(45deg, ${currentTheme.secondary} 0%, ${currentTheme.primary} 100%)`
            ],
          }}
          transition={{ duration: 3, repeat: Infinity, repeatType: 'reverse' }}
          className="w-100 h-100"
        />
      </div>
  
      <Container className="position-relative">
        <motion.div
          className="text-center mb-5"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            animate={{
              y: [-5, 5, -5],
              transition: { duration: 2, repeat: Infinity }
            }}
            className="mb-3"
          >
            <FaGraduationCap style={{ fontSize: '4rem', color: currentTheme.primary }} />
          </motion.div>
          <h2 className="display-4 fw-bold mb-1" style={{ color: currentTheme.primary }}>EDUCATION</h2>
          <div className="d-flex align-items-center justify-content-center gap-2 mb-4">
            <div style={{ backgroundColor: currentTheme.primary, height: '2px', width: '30px' }}></div>
            <FaUniversity style={{ color: currentTheme.primary }} />
            <div style={{ backgroundColor: currentTheme.primary, height: '2px', width: '30px' }}></div>
          </div>
        </motion.div>
  
        <Row className="align-items-start gy-5">
          <Col md={12} lg={6}>
            <motion.div {...fadeInUp}>
              <Card className="rounded-3 shadow-sm" style={{ backgroundColor: currentTheme.background === '#121212' ? '#222222' : 'rgba(255, 255, 255, 0.9)', borderColor: currentTheme.primary, borderWidth: '1px' }}>
                <Card.Body>
                  <div className="d-flex align-items-center mb-4">
                    <div className="me-4">
                      <FaUniversity style={{ fontSize: '3rem', color: currentTheme.primary }} />
                    </div>
                    <div>
                      <h3 className="h2 fw-bold mb-2 fs-4 lh-base" style={{ color: currentTheme.text }}>KCA University</h3>
                      <p className="mb-0 fw-semibold fs-5 lh-base" style={{ color: currentTheme.primary }}>
                        Bachelor of Science in Software Development
                      </p>
                      <p className="mb-0 fs-6 lh-base" style={{ color: currentTheme.text }}>September 2020 - December 2024</p>
                    </div>
                  </div>
  
                  <div className="ps-4 border-start border-3 mb-4" style={{ borderColor: currentTheme.primary }}>
                    <div className="d-flex align-items-center mb-3">
                      <FaAward style={{ color: currentTheme.primary }} className="me-2" />
                      <h4 className="h5 mb-0 fw-bold fs-6 lh-base" style={{ color: currentTheme.text }}>Second Class Honours (Upper Division)</h4>
                    </div>
                    <p className="fs-6 lh-base" style={{ color: currentTheme.text }}>
                      Specialized in full-stack development with a focus on modern web technologies and enterprise software solutions. Engaged in multiple practical projects implementing advanced software development principles.
                    </p>
                  </div>
  
                  <h4 className="fw-bold mb-4 fs-5 lh-base" style={{ color: currentTheme.primary }}>Notable Projects</h4>
                  {projects.map((project, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="d-flex mb-4"
                    >
                      <div className="p-3 rounded-circle me-3" style={{ backgroundColor: `rgba(${currentTheme.primary}, 0.1)` }}>
                        <FaProjectDiagram style={{ color: currentTheme.primary }} />
                      </div>
                      <div>
                        <h5 className="fw-bold mb-1 fs-6 lh-base" style={{ color: currentTheme.text }}>{project.title}</h5>
                        <p className="mb-0 fs-6 lh-base" style={{ color: currentTheme.text }}>{project.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </Card.Body>
              </Card>
            </motion.div>
          </Col>
  
          <Col md={12} lg={6}>
            <motion.div {...fadeInUp}>
              <Card className="rounded-3 shadow-sm mb-4" style={{ backgroundColor: currentTheme.background === '#121212' ? '#222222' : 'rgba(255, 255, 255, 0.9)', borderColor: currentTheme.primary, borderWidth: '1px' }}>
                <Card.Body>
                  <h4 className="fw-bold mb-4 d-flex align-items-center fs-5 lh-base" style={{ color: currentTheme.primary }}>
                    <FaLaptopCode className="me-2" style={{ color: currentTheme.primary }} />
                    Technical Proficiency
                  </h4>
                  {skills.map((skill, index) => (
                    <div key={index} className="mb-4">
                      <div className="d-flex justify-content-between mb-2">
                        <span className="fw-semibold fs-6 lh-base" style={{ color: currentTheme.text }}>{skill.name}</span>
                        <span className="fs-6 lh-base" style={{ color: currentTheme.primary }}>{skill.progress}%</span>
                      </div>
                      <div className="progress" style={{ height: '8px', backgroundColor: `rgba(${currentTheme.primary}, 0.1)` }}>
                        <motion.div
                          className="progress-bar"
                          initial={{ width: 0 }}
                          whileInView={{ width: `${skill.progress}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 1, delay: index * 0.1 }}
                          style={{ backgroundColor: currentTheme.primary }}
                        />
                      </div>
                    </div>
                  ))}
                </Card.Body>
              </Card>

              <Card className="rounded-3 shadow-sm" style={{ backgroundColor: currentTheme.background === '#121212' ? '#222222' : 'rgba(255, 255, 255, 0.9)', borderColor: currentTheme.primary, borderWidth: '1px' }}>
                <Card.Body>
                  <h4 className="fw-bold mb-4 fs-5 lh-base" style={{ color: currentTheme.primary }}>Areas of Expertise</h4>
                  {[
                    {
                      icon: FaCode,
                      title: "Advanced Software Development",
                      desc: "Mastered modern frameworks and architectures"
                    },
                    {
                      icon: FaDatabase,
                      title: "Database Management",
                      desc: "Expert in SQL and NoSQL database systems"
                    },
                    {
                      icon: FaProjectDiagram,
                      title: "System Design",
                      desc: "Proficient in creating scalable system architectures"
                    },
                    {
                      icon: FaBrain,
                      title: "Research & Innovation",
                      desc: "Proficient in handling research paper on emerging technologies"
                    }
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="d-flex mb-4"
                    >
                      <div className="p-3 rounded-circle me-3" style={{ backgroundColor: `rgba(${currentTheme.primary}, 0.1)` }}>
                        <item.icon style={{ color: currentTheme.primary }} />
                      </div>
                      <div>
                        <h5 className="fw-bold mb-1 fs-6 lh-base" style={{ color: currentTheme.text }}>{item.title}</h5>
                        <p className="mb-0 fs-6 lh-base" style={{ color: currentTheme.text }}>{item.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </Card.Body>
              </Card>
            </motion.div>
          </Col>
        </Row>
  
        <motion.div
          animate={{
            rotate: 360,
            transition: { duration: 20, repeat: Infinity, ease: "linear" }
          }}
          className="position-absolute"
          style={{ top: '10%', right: '5%', opacity: 0.1 }}
        >
          <FaCode style={{ fontSize: '4rem', color: currentTheme.primary }} />
        </motion.div>
      </Container>
    </Container>
  );
  
};

export default Education;